import { usePrintQueueMonitor } from "@/hooks/usePrintQueueMonitor";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  RefreshCw,
  RotateCcw,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  Printer,
  Loader2,
  Timer,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { MAX_RETRY_ATTEMPTS } from "@/lib/centralized-print-queue";

export function PrintQueueMonitor() {
  const {
    jobs,
    stats,
    isLoading,
    fetchJobs,
    retryJob,
    retryAllFailed,
    deleteJob,
    clearPrinted,
  } = usePrintQueueMonitor();

  const handleRetryJob = async (jobId: string) => {
    const success = await retryJob(jobId);
    if (success) {
      toast.success("Job reenviado para fila");
    } else {
      toast.error("Erro ao reenviar job");
    }
  };

  const handleRetryAll = async () => {
    const success = await retryAllFailed();
    if (success) {
      toast.success(`${stats.failed} job(s) reenviado(s)`);
    } else {
      toast.error("Erro ao reenviar jobs");
    }
  };

  const handleClearPrinted = async () => {
    const success = await clearPrinted();
    if (success) {
      toast.success("Histórico de impressões limpo");
    } else {
      toast.error("Erro ao limpar histórico");
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    const success = await deleteJob(jobId);
    if (success) {
      toast.success("Job removido");
    } else {
      toast.error("Erro ao remover job");
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case "printed":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-destructive" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">Aguardando</Badge>;
      case "printed":
        return <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">Impresso</Badge>;
      case "failed":
        return <Badge variant="destructive">Falhou</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Printer className="h-5 w-5" />
            Fila de Impressão
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchJobs}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Stats */}
        <div className="flex gap-4 text-sm">
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5 text-yellow-500" />
            <span className="text-muted-foreground">Pendentes:</span>
            <strong>{stats.pending}</strong>
          </span>
          <span className="flex items-center gap-1">
            <XCircle className="h-3.5 w-3.5 text-destructive" />
            <span className="text-muted-foreground">Falhas:</span>
            <strong className={stats.failed > 0 ? "text-destructive" : ""}>{stats.failed}</strong>
          </span>
          <span className="flex items-center gap-1">
            <CheckCircle className="h-3.5 w-3.5 text-green-500" />
            <span className="text-muted-foreground">Impressos:</span>
            <strong>{stats.printed}</strong>
          </span>
        </div>

        {/* Quick actions */}
        {(stats.failed > 0 || stats.printed > 0) && (
          <div className="flex gap-2 pt-2">
            {stats.failed > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRetryAll}
                className="text-xs"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Reenviar {stats.failed} falha(s)
              </Button>
            )}
            {stats.printed > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearPrinted}
                className="text-xs text-muted-foreground"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                Limpar impressos
              </Button>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent className="pt-0">
        {jobs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Printer className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Nenhum job na fila</p>
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            <div className="space-y-2">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  {getStatusIcon(job.status)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">
                        Pedido #{job.order_number}
                      </span>
                      {getStatusBadge(job.status)}
                      {job.location_name && (
                        <Badge variant="secondary" className="text-xs">
                          {job.location_name}
                        </Badge>
                      )}
                      {job.status === "pending" && (job.attempts || 0) > 0 && (
                        <Badge variant="outline" className="text-xs bg-orange-500/10 text-orange-600 border-orange-500/30">
                          <Timer className="h-3 w-3 mr-1" />
                          Tentativa {(job.attempts || 0) + 1}/{MAX_RETRY_ATTEMPTS}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      <span className="font-mono">{job.printer_name}</span>
                      <span className="mx-1">•</span>
                      <span>
                        {format(new Date(job.created_at), "dd/MM HH:mm:ss")}
                      </span>
                    </div>
                    {job.error_message && (
                      <div className="text-xs text-destructive mt-1 line-clamp-2">
                        {job.error_message}
                      </div>
                    )}
                    {job.printed_at && (
                      <div className="text-xs text-green-600 mt-1">
                        Impresso em {format(new Date(job.printed_at), "HH:mm:ss")}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {job.status === "failed" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleRetryJob(job.id)}
                        title="Tentar novamente"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {job.status !== "pending" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => handleDeleteJob(job.id)}
                        title="Remover"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
